import {
  isAuthoredByReader,
  isOwnReceipt,
  type MessageAuthorship,
  messageSide,
  receiptSide,
  type ThreadReader
} from '@/pages/CRM/ReferralOutbound/lib/callerIdentity';
import type { ReadReceipt } from '@/types/referral-outbound-center/models/ReadReceipt';

// A referral has exactly one recipient (m0859's
// uq_outbound_referral_participants_recipient), so the referral is the
// conversation. Everything here is scoped by referral alone — there is no
// per-thread filtering left to do.

export interface ThreadMessage extends MessageAuthorship {
  message_id: string;
}

export interface ReadState {
  firstReadAt: string | null;
}

// "Read" means the other side of the referral has opened it, not just that
// someone besides the reader has. A teammate on the reader's own side who
// opens the same thread should not make this look read by the recipient.
export const computeReadState = (
  message: ThreadMessage,
  receipts: ReadReceipt[],
  reader: ThreadReader | null
): ReadState | null => {
  if (!isAuthoredByReader(message, reader)) {
    return null;
  }

  const authorSide = messageSide(message);
  const eligible = receipts.filter(
    (receipt) =>
      receiptSide(receipt) !== authorSide &&
      // ULID lex order: last_read_message_id >= message_id means read.
      receipt.last_read_message_id >= message.message_id
  );
  if (eligible.length === 0) {
    return null;
  }

  const firstReadAt = eligible.map((r) => r.last_read_at).sort()[0];
  return { firstReadAt: firstReadAt ?? null };
};

export const shouldMarkAsRead = (
  latestMessageId: string,
  receipts: ReadReceipt[],
  reader: ThreadReader | null
): boolean => {
  if (!latestMessageId || !reader) {
    return false;
  }
  const myReceipt = receipts.find((receipt) => isOwnReceipt(receipt, reader));
  if (!myReceipt) {
    return true;
  }
  return latestMessageId > myReceipt.last_read_message_id;
};

export const isThreadUnread = (
  messages: ThreadMessage[],
  receipts: ReadReceipt[],
  reader: ThreadReader | null
): boolean => {
  const latest = messages[messages.length - 1];
  if (!latest || !reader) {
    return false;
  }
  if (isAuthoredByReader(latest, reader)) {
    return false;
  }
  const myReceipt = receipts.find((receipt) => isOwnReceipt(receipt, reader));
  if (!myReceipt) {
    return true;
  }
  return latest.message_id > myReceipt.last_read_message_id;
};

export const selectLatestSentId = (
  messages: ThreadMessage[],
  reader: ThreadReader | null
): string | null => {
  if (!reader) {
    return null;
  }
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (!message) {
      continue;
    }
    if (isAuthoredByReader(message, reader)) {
      return message.message_id;
    }
  }
  return null;
};
